import { useEffect, useMemo, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Link, useNavigate } from "react-router-dom";
import { getQuestionSet, listQuestionSets } from "@/api/question-sets";
import type { QuestionSetRead } from "@/api/library-types";
import { progressStore } from "@/practice/progress-store";
import { useProgressHydrated } from "@/practice/use-progress";
import { compose, type Focus } from "@/practice/session-composer";
import { setPendingSession } from "@/practice/session-handoff";
import { PersonalStatsBanner } from "@/stats/personal-stats-banner";
import { RewardRateStrip } from "@/stats/reward-rate-strip";
import { SendMinutesButton } from "@/community/send-minutes-modal";
import { Alert, Button, Input, Spinner, cx } from "@/components/ui";
import { clearSelectedSetId, loadSelectedSetId, saveSelectedSetId } from "./selected-set";
import { FrameworkBadge, SetCard, formatSetDate, frameworkLabel } from "./set-card";

interface FocusOption {
  value: string;
  label: string;
}

function buildFocusOptions(set: QuestionSetRead | undefined, t: TFunction): FocusOption[] {
  const opts: FocusOption[] = [{ value: "none", label: t("No focus") }];
  if (!set) return opts;
  opts.push({ value: `whole:${set.id}`, label: t("All of {{title}}", { title: set.title }) });
  const seen = new Set<string>();
  for (const b of [...set.bulks].sort((a, b) => a.order - b.order)) {
    if (seen.has(b.topic_slug)) continue;
    seen.add(b.topic_slug);
    opts.push({ value: `topic:${set.id}:${b.topic_slug}`, label: `  ${b.topic_label}` });
  }
  return opts;
}

/** Unique, ordered topic labels for a full set (for the card/hero preview). */
function topicLabels(set: QuestionSetRead): string[] {
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const b of [...set.bulks].sort((a, b) => a.order - b.order)) {
    if (seen.has(b.topic_slug)) continue;
    seen.add(b.topic_slug);
    labels.push(b.topic_label);
  }
  return labels;
}

function parseFocus(v: string): Focus {
  if (v.startsWith("whole:")) return { kind: "wholeSet", setId: v.slice(6) };
  if (v.startsWith("topic:")) {
    const rest = v.slice(6);
    const sep = rest.indexOf(":");
    return { kind: "topic", setId: rest.slice(0, sep), topicSlug: rest.slice(sep + 1) };
  }
  return { kind: "none" };
}

function Stepper({
  value,
  min,
  max,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="inline-flex items-center gap-3">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        className="flex h-8 w-8 items-center justify-center rounded-lg bg-black/5 text-lg font-bold text-ink-soft hover:bg-black/10 disabled:opacity-30"
      >
        −
      </button>
      <span className="w-8 text-center font-mono font-bold">{value}</span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        className="flex h-8 w-8 items-center justify-center rounded-lg bg-black/5 text-lg font-bold text-ink-soft hover:bg-black/10 disabled:opacity-30"
      >
        +
      </button>
    </div>
  );
}

export function TrainPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const hydrated = useProgressHydrated();

  const setsQuery = useQuery({ queryKey: ["question-sets"], queryFn: () => listQuestionSets(false) });

  // Single active set, remembered across reloads/navigation.
  const [selectedId, setSelectedId] = useState<string | null>(() => loadSelectedSetId());

  const [mode, setMode] = useState<"normal" | "wrongOnly">("normal");
  const [focusVal, setFocusVal] = useState("none");
  const [sections, setSections] = useState(1);
  const [hideSeen, setHideSeen] = useState(false);
  const [drillCount, setDrillCount] = useState(10);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  // Drop a remembered id that no longer exists (set deleted/archived elsewhere).
  useEffect(() => {
    if (!selectedId || !setsQuery.data) return;
    if (!setsQuery.data.some((s) => s.id === selectedId)) {
      clearSelectedSetId();
      setSelectedId(null);
    }
  }, [selectedId, setsQuery.data]);

  const choose = (id: string) => {
    setSelectedId(id);
    saveSelectedSetId(id);
    setFocusVal("none");
    setError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Background-fetch every set in full so the cards can show topic previews and
  // the active set's controls have its bulks. React Query dedupes + caches, so
  // the active set is fetched once even though it appears here and in the hero.
  const setIds = useMemo(() => setsQuery.data?.map((s) => s.id) ?? [], [setsQuery.data]);
  const fullQueries = useQueries({
    queries: setIds.map((id) => ({
      queryKey: ["question-set", id],
      queryFn: () => getQuestionSet(id),
      enabled: hydrated && setIds.length > 0,
      staleTime: 5 * 60_000,
    })),
  });
  const topicsBySet = useMemo(() => {
    const m: Record<string, string[]> = {};
    for (const q of fullQueries) {
      if (q.data) m[q.data.id] = topicLabels(q.data);
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullQueries.map((q) => q.data?.id).join(",")]);

  const fullSet = fullQueries
    .map((q) => q.data)
    .find((d): d is QuestionSetRead => !!d && d.id === selectedId);

  // Sets containing crossword puzzles → the gamified "Puzzles" section.
  // Discovered cheaply from the summary `has_puzzles` flag (no full-set fetch).
  const puzzleSets = useMemo(
    () => (setsQuery.data ?? []).filter((s) => s.has_puzzles),
    [setsQuery.data],
  );

  // Lazily fetch the full set on Play (the summary has no items), then compose
  // a puzzle-only session.
  const playPuzzles = async (setId: string) => {
    setError(null);
    try {
      const set = await getQuestionSet(setId);
      const composed = compose({
        sets: [set],
        mode: "puzzleOnly",
        bulkCount: 0,
        wrongLimit: 0,
        focus: { kind: "wholeSet", setId: set.id },
        hideSeenInfo: false,
      });
      if (composed.items.length === 0) {
        setError(composed.diagnostics[0] ?? t("No crossword puzzles in the selected sets."));
        return;
      }
      setPendingSession(composed);
      navigate("/app/train/play");
    } catch {
      setError(t("Couldn't load that puzzle set."));
    }
  };

  const focusOptions = useMemo(() => buildFocusOptions(fullSet, t), [fullSet, t]);
  const maxBulks = useMemo(() => {
    if (!fullSet) return 1;
    const done = progressStore.completedBulkIds(fullSet.id);
    return Math.max(1, fullSet.bulks.filter((b) => !done.has(b.id)).length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullSet, hydrated]);
  const wrongCount = useMemo(() => {
    return fullSet ? progressStore.wrongIds(fullSet.id).size : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullSet, hydrated]);
  const continueLabel = useMemo(() => {
    if (!fullSet) return null;
    const c = progressStore.cursor(fullSet.id);
    if (!c) return null;
    return fullSet.bulks.find((b) => b.id === c.bulkId)?.topic_label ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullSet, hydrated]);

  useEffect(() => setSections((s) => Math.min(Math.max(1, s), maxBulks)), [maxBulks]);
  useEffect(
    () => setDrillCount((c) => Math.min(Math.max(1, c), Math.max(1, wrongCount))),
    [wrongCount],
  );
  useEffect(() => {
    if (!focusOptions.some((o) => o.value === focusVal)) setFocusVal("none");
  }, [focusOptions, focusVal]);

  const begin = () => {
    if (!fullSet) return;
    setError(null);
    const composed = compose({
      sets: [fullSet],
      mode: "normal",
      bulkCount: sections,
      wrongLimit: 0,
      focus: parseFocus(focusVal),
      hideSeenInfo: hideSeen,
    });
    if (composed.items.length === 0) {
      setError(composed.diagnostics[0] ?? t("Nothing to practise."));
      return;
    }
    setPendingSession(composed);
    navigate("/app/train/play");
  };

  const drill = () => {
    if (!fullSet) return;
    setError(null);
    const composed = compose({
      sets: [fullSet],
      mode: "wrongOnly",
      bulkCount: 0,
      wrongLimit: drillCount,
      focus: { kind: "none" },
      hideSeenInfo: false,
    });
    if (composed.items.length === 0) {
      setError(composed.diagnostics[0] ?? t("No wrong answers to drill yet."));
      return;
    }
    setPendingSession(composed);
    navigate("/app/train/play");
  };

  const sets = setsQuery.data ?? [];
  const summary = sets.find((s) => s.id === selectedId);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? sets.filter((s) => s.title.toLowerCase().includes(q)) : sets;
  }, [sets, query]);

  return (
    // Break out of the shell's max-w-6xl container to use the full window width.
    <div className="mx-[calc(50%-50vw)] animate-fade-up px-5 sm:px-8">
      <div className="mx-auto max-w-[1600px]">
        <h1 className="text-3xl font-bold tracking-tight">{t("Train")}</h1>
        <p className="mt-1 text-ink-faint">
          {t("Pick a set and practise. We'll remember where you left off.")}
        </p>

        <div className="mt-5">
          <PersonalStatsBanner />
          <RewardRateStrip />
          <div className="mt-3 flex justify-end">
            <SendMinutesButton />
          </div>
        </div>

        {setsQuery.isLoading && (
          <div className="flex justify-center py-16 text-brand-600">
            <Spinner className="h-6 w-6" />
          </div>
        )}

        {setsQuery.data && sets.length === 0 && (
          <div className="mt-10 text-center">
            <p className="text-ink-faint">{t("No sets yet — generate one first.")}</p>
            <Link to="/app/create" className="mt-4 inline-block">
              <Button>{t("Go to Create")}</Button>
            </Link>
          </div>
        )}

        {sets.length > 0 && (
          <>
            {/* "Now training" hero — full width, controls on the right. */}
            {selectedId && summary && (
              <div className="card mt-6 p-6">
                <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
                  {/* identity + continue */}
                  <div className="min-w-0">
                    <div className="flex items-start gap-3">
                      <FrameworkBadge slug={summary.source_framework_slug} />
                      <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">
                          {t("Now training")}
                        </p>
                        <h2 className="truncate text-2xl font-bold tracking-tight text-ink">
                          {summary.title}
                        </h2>
                        <p className="mt-0.5 text-sm text-ink-faint">
                          {frameworkLabel(summary.source_framework_slug, t)} ·{" "}
                          {summary.item_count === 1
                            ? t("{{count}} item", { count: summary.item_count })
                            : t("{{count}} items", { count: summary.item_count })}{" "}
                          ·{" "}
                          {summary.bulk_count === 1
                            ? t("{{count}} topic", { count: summary.bulk_count })
                            : t("{{count}} topics", { count: summary.bulk_count })}
                          {summary.created_at && ` · ${formatSetDate(summary.created_at)}`}
                        </p>
                      </div>
                    </div>

                    {summary.description && (
                      <p className="mt-3 text-sm text-ink-soft">{summary.description}</p>
                    )}

                    <p className="mt-3 text-sm text-ink-soft">
                      {continueLabel ? (
                        <>
                          <span className="font-medium">{t("Continue where you left off")}</span> ·{" "}
                          {continueLabel}
                        </>
                      ) : (
                        <span className="text-ink-faint">{t("Not started yet — fresh set.")}</span>
                      )}
                    </p>

                    {topicsBySet[summary.id]?.length ? (
                      <p className="mt-2 text-sm text-ink-faint">
                        <span className="font-medium text-ink-soft">{t("Topics:")}</span>{" "}
                        {topicsBySet[summary.id].slice(0, 6).join(" · ")}
                        {topicsBySet[summary.id].length > 6
                          ? ` +${topicsBySet[summary.id].length - 6}`
                          : ""}
                      </p>
                    ) : null}
                  </div>

                  {/* controls */}
                  <div className="lg:border-l lg:border-black/5 lg:pl-6">
                    {error && (
                      <div className="mb-3">
                        <Alert>{error}</Alert>
                      </div>
                    )}

                    <div className="mb-4 inline-flex rounded-xl bg-black/5 p-1">
                      {(["normal", "wrongOnly"] as const).map((m) => (
                        <button
                          key={m}
                          onClick={() => setMode(m)}
                          className={cx(
                            "rounded-lg px-3 py-1.5 text-sm font-semibold transition",
                            mode === m ? "bg-white text-brand-700 shadow-soft" : "text-ink-faint",
                          )}
                        >
                          {m === "normal" ? t("Practice") : t("Drill Wrongs")}
                        </button>
                      ))}
                    </div>

                    {!fullSet ? (
                      <div className="flex justify-center py-6 text-brand-600">
                        <Spinner className="h-5 w-5" />
                      </div>
                    ) : mode === "normal" ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-ink-soft">{t("Sections")}</span>
                          <Stepper value={sections} min={1} max={maxBulks} onChange={setSections} />
                        </div>

                        <button
                          type="button"
                          onClick={() => setCustomizeOpen((v) => !v)}
                          className="flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700"
                        >
                          <span>{customizeOpen ? "▾" : "▸"}</span> {t("Customize")}
                        </button>
                        {customizeOpen && (
                          <div className="space-y-4 rounded-xl bg-black/[0.03] p-4">
                            <label className="flex items-center justify-between gap-4">
                              <span className="text-sm font-medium text-ink-soft">{t("Focus")}</span>
                              <select
                                value={focusVal}
                                onChange={(e) => setFocusVal(e.target.value)}
                                className="max-w-[14rem] flex-1 truncate rounded-xl border border-black/10 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                              >
                                {focusOptions.map((o) => (
                                  <option key={o.value} value={o.value}>
                                    {o.label}
                                  </option>
                                ))}
                              </select>
                            </label>

                            <label className="flex cursor-pointer items-center gap-3">
                              <input
                                type="checkbox"
                                checked={hideSeen}
                                onChange={(e) => setHideSeen(e.target.checked)}
                                className="h-5 w-5 rounded border-black/20 text-brand-600 focus:ring-brand-400"
                              />
                              <span className="text-sm text-ink-soft">
                                {t("Hide already-seen info cards")}
                              </span>
                            </label>
                          </div>
                        )}

                        <Button size="lg" className="w-full hover:shadow-glow" onClick={begin}>
                          {t("Begin")}
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <p className="text-sm text-ink-faint">
                          {wrongCount > 0
                            ? wrongCount === 1
                              ? t("{{count}} wrong answer ready to retry.", { count: wrongCount })
                              : t("{{count}} wrong answers ready to retry.", { count: wrongCount })
                            : t("No wrong answers in this set yet — practise first.")}
                        </p>
                        {wrongCount > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-ink-soft">{t("How many")}</span>
                            <Stepper
                              value={drillCount}
                              min={1}
                              max={Math.max(1, wrongCount)}
                              onChange={setDrillCount}
                            />
                          </div>
                        )}
                        <Button
                          size="lg"
                          className="w-full"
                          disabled={wrongCount === 0}
                          onClick={drill}
                        >
                          {wrongCount > 0
                            ? drillCount === 1
                              ? t("Drill {{count}} wrong", { count: drillCount })
                              : t("Drill {{count}} wrongs", { count: drillCount })
                            : t("Drill wrongs")}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Puzzles — gamified crossword-only sessions. */}
            {puzzleSets.length > 0 && (
              <div className="mt-8">
                <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-indigo-500">
                  <span aria-hidden>🧩</span> {t("Puzzles")}
                </h2>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {puzzleSets.map((s) => {
                    const count = s.puzzle_count ?? 0;
                    return (
                      <div
                        key={s.id}
                        className="card flex items-center justify-between gap-3 border-indigo-200 bg-indigo-50/40 p-4"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-ink">{s.title}</p>
                          <p className="text-xs text-ink-faint">
                            {count === 1
                              ? t("{{count}} crossword", { count })
                              : t("{{count}} crosswords", { count })}
                          </p>
                        </div>
                        <Button size="sm" onClick={() => playPuzzles(s.id)}>
                          {t("Play")}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* "Your sets" grid — the integrated picker (no modal). */}
            <div className="mt-8">
              <div className="mb-3 flex items-center justify-between gap-4">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-faint">
                  {selectedId ? t("Your sets") : t("Choose a set to train")}
                </h2>
                {sets.length > 8 && (
                  <div className="w-56">
                    <Input
                      placeholder={t("Search sets…")}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filtered.map((s) => (
                  <SetCard
                    key={s.id}
                    summary={s}
                    active={s.id === selectedId}
                    topics={topicsBySet[s.id]}
                    onSelect={() =>
                      s.id === selectedId ? navigate(`/app/library/${s.id}`) : choose(s.id)
                    }
                  />
                ))}
              </div>
              {filtered.length === 0 && (
                <p className="py-6 text-center text-sm text-ink-faint">{t("No sets match “{{query}}”.", { query })}</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
